import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import * as XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

//import components
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/DataTableContainer4";

const DatatableTables = () => {
  const navigate = useNavigate();
  const [type, setType] = useState("1");
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [startDate, setStartDate] = useState(() => moment().subtract(6, "days").format("YYYY-MM-DD"));
  const [startDateError, setStartDateError] = useState("");
  const [endDate, setEndDate] = useState(() => moment().format("YYYY-MM-DD"));
  const [endDateError, setEndDateError] = useState("");
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortingBy, setSortingBy] = useState("Id");
  const [direction, setDirection] = useState("Desc");
  const [search, setSearch] = useState("");
  const token = localStorage.getItem("authToken");
  const user = JSON.parse(localStorage.getItem("authUser"));

  const setDatesByType = useCallback((selectedType) => {
    const today = moment();
    let start = "";

    switch (selectedType) {
      case "1":
        start = today.clone().subtract(6, "days").format("YYYY-MM-DD");
        break;

      case "2":
        start = today.clone().subtract(29, "days").format("YYYY-MM-DD");
        break;

      case "3":
        start = today.clone().subtract(89, "days").format("YYYY-MM-DD");
        break;

      default:
        start = "";
    }

    setStartDate(start);
    setEndDate(today.format("YYYY-MM-DD"));

  }, []);

  const countWeekdaysInRange = (startDate, endDate, dayName) => {

    if (!startDate || !endDate || !dayName) return 0;

    let count = 0;

    const start = moment(startDate);
    const end = moment(endDate);

    while (start.isSameOrBefore(end, "day")) {

      if (start.format("dddd") === dayName) {
        count++;
      }

      start.add(1, "day");
    }

    return count;
  };

  const fetchTableData = useCallback(async () => {
    if (type === "4") {
      if (!startDate && !endDate) {
        setStartDateError("Start date is required");
        setEndDateError("End date is required");
        return;
      } else {
        setStartDateError("");
        setEndDateError("");
      }
      if (!startDate) {
        setStartDateError("Start date is required");
        return;
      } else {
        setStartDateError("");
      }
      if (!endDate) {
        setEndDateError("End date is required");
        return;
      } else {
        setEndDateError("");
      }
    }

    try {
      setLoading(true);

      const params = {
        startDate,
        endDate,
        page,
        countPerPage,
        sortingBy,
        direction,
        search,
      };

      let apiUrl = "";

      if (user?.Role === "Student") {
        apiUrl = "https://api.kunalsdrumclass.com/api/GetStudentSummaryReportByStudentId";
        params.studentId = user?.Id;
      } else if (selectedStudent) {
        apiUrl = "https://api.kunalsdrumclass.com/api/GetStudentSummaryReportByStudentId";
        params.studentId = selectedStudent?.value;
      } else {
        apiUrl = "https://api.kunalsdrumclass.com/api/GetAllStudentSummaryReport";
      }

      const query = new URLSearchParams(params).toString();
      const res = await fetch(`${apiUrl}?${query}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = await res.json();
      const list = result?.recordsets?.[0] || [];
      const total = result?.recordsets?.[1]?.[0]?.TotalRows || 0;
      setTableData(
        list.map((item, index) => {

          const monthStart = moment().startOf("month").format("YYYY-MM-DD");
          const monthEnd = moment().endOf("month").format("YYYY-MM-DD");

          const totalClasses = countWeekdaysInRange(
            monthStart,
            monthEnd,
            item.Day_Of_Week
          );

          const percentage =
            totalClasses > 0
              ? (
                (item.Fix_Class_Attended_Count / totalClasses) *
                100
              ).toFixed(2)
              : 0;

          return {
            ...item,
            index: (page - 1) * countPerPage + index + 1,
            Total_Classes: totalClasses,
            Attendance_Ratio: `${item.Fix_Class_Attended_Count}/${totalClasses}`,
            Attendance_Percentage: percentage,
          };

        })
      );
      setTotalRecords(total);

    } catch (error) {
      console.error("API Error:", error);
    } finally {
      setLoading(false);
    }

  }, [type, startDate, endDate, page, countPerPage, sortingBy, direction, search, token, selectedStudent]);

  const fetchData = useCallback(async () => {
    try {
      const fetchList = async (url) => {
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        return json?.recordsets?.[0] || [];
      };

      setStudents(await fetchList("https://api.kunalsdrumclass.com/api/GetActiveStudents"));

    } catch (error) {
      console.error("Fetch error", error);
    }
  }, []);

  useEffect(() => {
    if (!type) return;

    fetchTableData();
  }, [fetchTableData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getAttendanceStyle = (percentage) => {
    if (percentage < 50) return "text-danger fw-bold";
    if (percentage < 75) return "text-warning fw-bold";
    return "text-success fw-bold";
  };

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (size) => {
    setCountPerPage(size);
    setPage(1);
  };

  const handleSortChange = (columnId, desc) => {
    if (!columnId) {
      setSortingBy(null);
      setDirection("Asc");
    } else {
      setSortingBy(columnId);
      setDirection(desc ? "Desc" : "Asc");
    }
    setPage(1);
  };

  const handleSearch = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  const sortingState = useMemo(() => {
    if (!sortingBy) return [];
    return [{ id: sortingBy, desc: direction === "Desc" }];
  }, [sortingBy, direction]);

  const studentOptions = students.map((item) => ({
    value: item.Id,
    label: item.Student_Name,
    loginId: item.Login_Id,
  }));

  // Excel Export
  const exportToExcel = () => {
    if (!tableData.length) return;

    const worksheetData = tableData.map((item) => {
      let color = "000000";

      if (item.Attendance_Percentage < 50) color = "FF0000";
      else if (item.Attendance_Percentage < 75) color = "FFA500";
      else color = "008000";

      return {
        Id: item.Id,
        Name: item.Student_Name,
        "Payment Status": item.Late_Fee_Status,
        "Attendance Ratio": item.Attendance_Ratio,
        "Attendance %": {
          v: `${item.Attendance_Percentage}%`,
          s: {
            font: {
              color: { rgb: color },
              bold: true,
            },
          },
        },
        "Fix Class Attended": item.Fix_Class_Attended_Count,
        "Comp Taken": item.Comp_Class_Taken_Count,
        "Comp Pending": item.Comp_Class_Pending_Count,
        "Close Class Comp": item.Close_Class_Count,
        "No Show": item.No_Show_Count,
        Cancellation: item.Cancellation_Count,
        Swaps: item.Swap_Class_Count,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "SummaryReport");

    XLSX.writeFile(workbook, "SummaryReport.xlsx");
  };

  // PDF Export
  const exportToPDF = () => {
    if (!tableData.length) return;

    const doc = new jsPDF();

    const tableColumn = [
      "Id",
      "Name",
      "Payment",
      "Attendance Ratio",
      "Attendance %",
      "Fix Attended",
      "Comp Taken",
      "Comp Pending",
      "Close Class Comp",
      "No Show",
      "Cancellation",
      "Swaps",
    ];

    const tableRows = tableData.map((item) => [
      item.Id,
      item.Student_Name,
      item.Late_Fee_Status,
      item.Attendance_Ratio,
      `${item.Attendance_Percentage}%`,
      item.Fix_Class_Attended_Count,
      item.Comp_Class_Taken_Count,
      item.Comp_Class_Pending_Count,
      item.Close_Class_Count,
      item.No_Show_Count,
      item.Cancellation_Count,
      item.Swap_Class_Count,
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 160, 133] },
      didParseCell: function (data) {
        if (data.column.index === 4) {
          const value = parseFloat(data.cell.raw);

          if (value < 50) {
            data.cell.styles.textColor = [255, 0, 0];
          } else if (value < 75) {
            data.cell.styles.textColor = [255, 165, 0];
          } else {
            data.cell.styles.textColor = [0, 128, 0];
          }
        }
      },
    });

    doc.text("Summary Report", 14, 15);
    doc.save("SummaryReport.pdf");
  };

  const columns = useMemo(
    () => [
      {
        header: "Id",
        accessorKey: "Id",
        enableSorting: true,
      },
      {
        header: "Name",
        meta: {
          sticky: true,
          width: "125px",
        },
        accessorKey: "Student_Name",
        enableSorting: true,
      },
      {
        header: "Contact No",
        accessorKey: "Contact_Number",
        enableSorting: true,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rowData = row.original;
          const mobile = rowData.Contact_Number;
          const cleanNumber = mobile.replace(/\D/g, "");
          const email = rowData.Email_Address;

          return (
            <div className="d-flex flex-column gap-1">
              <span>{rowData.Contact_Number}</span>
              <div className="d-flex gap-2 align-items-center">
                <a
                  href={`tel:+${cleanNumber}`}
                  title="Call"
                >
                  <i className="mdi mdi-phone-outline fs-3"></i>
                </a>
                <a
                  href={`https://wa.me/${cleanNumber}`}
                  target="_blank"
                  rel="noreferrer"
                  title="WhatsApp"
                >
                  <i className="mdi mdi-whatsapp fs-3"></i>
                </a>
                <a
                  href={
                    /Mobi|Android/i.test(navigator.userAgent)
                      ? `mailto:${email}`
                      : `https://mail.google.com/mail/?view=cm&fs=1&to=${email}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Send Email"
                >
                  <i className="mdi mdi-email-send-outline fs-3"></i>
                </a>

              </div>
            </div>
          );
        },
      },
      {
        header: "Payment Status",
        accessorKey: "Late_Fee_Status",
        enableSorting: true,
      },
      {
        header: "Attendance Ratio",
        accessorKey: "Attendance_Ratio",
        enableSorting: false,
        cell: (info) => (
          <span className="fw-bold">
            {info.row.original.Attendance_Ratio}
          </span>
        ),
      },
      {
        header: "Attendance %",
        accessorKey: "Attendance_Percentage",
        enableSorting: false,
        cell: (info) => {

          const value = info.row.original.Attendance_Percentage;

          return (
            <span className={getAttendanceStyle(value)}>
              {value}%
            </span>
          );

        },
      },
      {
        header: "Fix Class",
        accessorKey: "Fix_Class_Attended_Count",
        enableSorting: true,
      },
      {
        header: "Comp Taken",
        accessorKey: "Comp_Class_Taken_Count",
        enableSorting: true,
      },
      {
        header: "Comp Pending",
        accessorKey: "Comp_Class_Pending_Count",
        enableSorting: true,
      },
      {
        header: "Close Class Comp",
        accessorKey: "Close_Class_Count",
        enableSorting: true,
      },
      {
        header: "No Show",
        accessorKey: "No_Show_Count",
        enableSorting: true,
      },
      {
        header: "Cancellation",
        accessorKey: "Cancellation_Count",
        enableSorting: true,
      },
      {
        header: "Swap Count",
        accessorKey: "Swap_Class_Count",
        enableSorting: true,
      },
    ],
    []
  );

  //meta title
  document.title = "Summary Report | KDC - Admin";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Summary Report"
          breadcrumbItems={[
            { title: "Reports", link: "/reports" },
            { title: "Summary Report" },
          ]}
        />

        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            Back to List
          </button>
        </div>

        <form className="mb-5">
          <div className="mb-3 mt-3">
            <div className="d-flex justify-content-center gap-2 mb-3">
              <button
                type="button"
                className={`btn ${type === "1" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => {
                  setType("1");
                  setPage(1);
                  setDatesByType("1");
                }}
              >
                Weekly
              </button>

              <button
                type="button"
                className={`btn ${type === "2" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => {
                  setType("2");
                  setPage(1);
                  setDatesByType("2");
                }}
              >
                Monthly
              </button>

              <button
                type="button"
                className={`btn ${type === "3" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => {
                  setType("3");
                  setPage(1);
                  setDatesByType("3");
                }}
              >
                Quarterly
              </button>

              <button
                type="button"
                className={`btn ${type === "4" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => {
                  setType("4");
                  setPage(1);
                  setStartDate("");
                  setEndDate("");
                }}
              >
                Custom
              </button>
            </div>
          </div>

          {type === "4" && (
            <div className="row">
              <div className="col-md-2 mb-3"></div>
              <div className="col-lg-4 mb-3">
                <label>Start Date*</label>
                <div className="input-group">
                  <Flatpickr
                    className="form-control d-block"
                    placeholder="dd-mm-yyyy"
                    value={
                      startDate
                        ? moment(startDate, "YYYY-MM-DD").format("DD-MM-YYYY")
                        : ""
                    }
                    options={{
                      dateFormat: "d-m-Y"
                    }}
                    onChange={(selectedDates) => {
                      if (selectedDates.length) {
                        setStartDate(moment(selectedDates[0]).format("YYYY-MM-DD"));
                      }
                    }}
                    required
                  />
                  <div className="input-group-append">
                    <span className="input-group-text">
                      <i className="mdi mdi-calendar" />
                    </span>
                  </div>
                </div>

                {startDateError && <p className="text-danger">{startDateError}</p>}
              </div>
              <div className="col-lg-4 mb-3">
                <label>End Date*</label>
                <div className="input-group">
                  <Flatpickr
                    className="form-control d-block"
                    placeholder="dd-mm-yyyy"
                    value={
                      endDate
                        ? moment(endDate, "YYYY-MM-DD").format("DD-MM-YYYY")
                        : ""
                    }
                    options={{
                      dateFormat: "d-m-Y"
                    }}
                    onChange={(selectedDates) => {
                      if (selectedDates.length) {
                        setEndDate(moment(selectedDates[0]).format("YYYY-MM-DD"));
                      }
                    }}
                    required
                  />
                  <div className="input-group-append">
                    <span className="input-group-text">
                      <i className="mdi mdi-calendar" />
                    </span>
                  </div>
                </div>
                {endDateError && <p className="text-danger">{endDateError}</p>}
              </div>
              <div className="col-md-2 mb-3"></div>
            </div>
          )}

          {type && user?.Role !== "Student" && (
            <div className="row">
              <div className="col-md-4 mb-3"></div>
              <div className="col-lg-4 mb-3">
                <div className="form-group">
                  <Select
                    value={selectedStudent}
                    onChange={(selectedOption) => {
                      setSelectedStudent(selectedOption);
                      setPage(1);
                    }}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 5,
                      }),
                    }}
                    menuPosition="fixed"
                    isClearable={true}
                    options={studentOptions}
                    classNamePrefix="select2-selection"
                    placeholder="Select Student"
                  />
                </div>
              </div>
              <div className="col-md-4 mb-3"></div>
            </div>
          )}
        </form>

        {type && (
          <>
            <div className="mb-3 text-end">
              <button
                type="button"
                className="btn btn-success me-2"
                onClick={exportToExcel}
              >
                <i className="mdi mdi-microsoft-excel me-1"></i>  Export Excel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={exportToPDF}
              >
                <i className="mdi mdi-file-pdf-outline me-1"></i>  Export PDF
              </button>
            </div>

            <TableContainer
              columns={columns}
              data={tableData}
              page={page}
              countPerPage={countPerPage}
              totalRecords={totalRecords}
              sortingState={sortingState}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onSortChange={handleSortChange}
              searchValue={search}
              onSearch={handleSearch}
              isLoading={loading}
              isPagination
              isGlobalFilter
              isCustomPageSize
              SearchPlaceholder="search..."
              pagination="pagination"
              paginationWrapper="dataTables_paginate paging_simple_numbers"
              tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default DatatableTables;